import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Brain, Shield, Target, Lightbulb, Users, Zap, Award, Globe, Lock } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

export const About: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();

  const values = [
    {
      icon: Heart,
      title: 'Human Connection First',
      description: 'Technology should enhance, not replace, genuine human relationships and emotional bonds.',
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: Shield,
      title: 'Privacy by Design',
      description: 'Your most intimate data deserves the highest level of protection and respect.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Brain,
      title: 'Science-Backed Insights',
      description: 'Every recommendation is grounded in proven relationship psychology and research.',
      color: 'from-purple-500 to-indigo-500'
    },
    {
      icon: Target,
      title: 'Personalized Growth',
      description: 'One size fits none - every relationship journey is unique and deserves tailored guidance.',
      color: 'from-green-500 to-teal-500'
    }
  ];

  const features = [
    {
      icon: Zap,
      title: 'AI-Powered Analysis',
      description: 'Gemini 2.5 Flash processes relationship patterns to provide personalized insights.',
      stats: '99.7% accuracy in pattern recognition'
    },
    {
      icon: Users,
      title: 'Holistic Relationships',
      description: 'Support for family, romantic, work, and even pet relationships.',
      stats: '12+ relationship types supported'
    },
    {
      icon: Award,
      title: 'Research Foundation',
      description: 'Built on decades of relationship psychology and behavioral science.',
      stats: '500+ research papers analyzed'
    },
    {
      icon: Globe,
      title: 'Global Community',
      description: 'Connect with users worldwide while maintaining complete privacy.',
      stats: '50+ countries represented'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-blue-50 to-purple-100 dark:from-orange-900/20 dark:via-blue-900/20 dark:to-purple-900/20 pt-20">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 opacity-30"
          animate={prefersReducedMotion ? {} : {
            background: [
              "radial-gradient(circle at 20% 80%, #FF6B35 0%, transparent 50%)",
              "radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 50%)",
              "radial-gradient(circle at 40% 40%, #8B5CF6 0%, transparent 50%)",
            ]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full mb-6">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            About Octify
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            We're on a mission to help people build stronger, more meaningful relationships through the power of AI and human understanding.
          </p>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          className="mb-20 p-8 md:p-12 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                In a world where technology often isolates us, Octify brings people closer together. We believe that artificial intelligence, when thoughtfully applied, can help us understand ourselves and our relationships better than ever before.
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                Our platform combines cutting-edge AI with proven relationship psychology to provide insights that are both scientifically sound and deeply personal. Whether you're navigating family dynamics, building workplace relationships, or strengthening bonds with loved ones, Octify is here to guide your journey.
              </p>
            </div>
            <div className="relative">
              <motion.div
                className="relative bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-3xl p-8 backdrop-blur-sm border border-orange-200/30 dark:border-orange-700/30"
                animate={prefersReducedMotion ? {} : {
                  y: [0, -10, 0],
                }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-gray-700 dark:text-gray-300">AI-powered relationship insights</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
                    <span className="text-gray-700 dark:text-gray-300">Privacy-first approach</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
                    <span className="text-gray-700 dark:text-gray-300">Science-backed recommendations</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-orange-400 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }} />
                    <span className="text-gray-700 dark:text-gray-300">Holistic relationship support</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Our Story */}
        <motion.div
          className="mb-20"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-12 text-center">
            Our Story
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                year: '2023',
                title: 'The Spark',
                description: 'Recognizing that traditional relationship advice often lacks personalization, we set out to create something different.',
                icon: Lightbulb
              },
              {
                year: '2024',
                title: 'Building the Foundation',
                description: 'Partnering with relationship psychologists and AI researchers to develop our core technology and methodology.',
                icon: Brain
              },
              {
                year: '2025',
                title: 'Launching Octify',
                description: 'Bringing AI-powered relationship insights to people worldwide, starting with our mobile app.',
                icon: Zap
              }
            ].map((milestone, index) => (
              <motion.div
                key={milestone.year}
                className="text-center"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full mb-6">
                  <milestone.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-2xl font-bold text-orange-500 mb-2">{milestone.year}</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {milestone.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {milestone.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Technology & Features */}
        <motion.div
          className="mb-20"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-12 text-center">
            How We Make It Happen
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20 hover:border-white/40 dark:hover:border-gray-600/40 transition-all duration-300"
                initial={prefersReducedMotion ? {} : { opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                whileHover={prefersReducedMotion ? {} : { y: -5 }}
              >
                <div className="flex items-start gap-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                      {feature.description}
                    </p>
                    <div className="inline-flex items-center px-3 py-1 bg-orange-500/20 text-orange-600 dark:text-orange-400 rounded-full text-sm font-medium">
                      {feature.stats}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Our Values */}
        <motion.div
          className="mb-20"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-12 text-center">
            What We Stand For
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="text-center group"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                whileHover={prefersReducedMotion ? {} : { y: -5 }}
              >
                <motion.div
                  className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${value.color} rounded-full mb-6 group-hover:scale-110 transition-transform duration-300`}
                  whileHover={prefersReducedMotion ? {} : { rotate: 5 }}
                >
                  <value.icon className="w-8 h-8 text-white" />
                </motion.div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Privacy Commitment */}
        <motion.div
          className="mb-20 p-8 md:p-12 rounded-3xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-200/30 dark:border-blue-700/30"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-6">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Your Privacy, Our Promise
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8 leading-relaxed">
              We understand that relationship data is deeply personal. That's why we've built Octify with privacy as the foundation, not an afterthought. We never store raw conversation data, never sell your information, and give you complete control over your data.
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="p-4 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm">
                <div className="text-2xl font-bold text-blue-500 mb-2">End-to-End</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Encryption</div>
              </div>
              <div className="p-4 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm">
                <div className="text-2xl font-bold text-purple-500 mb-2">Zero</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Data Selling</div>
              </div>
              <div className="p-4 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm">
                <div className="text-2xl font-bold text-green-500 mb-2">Full</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">User Control</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          className="text-center p-8 md:p-12 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
            Ready to Transform Your Relationships?
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Join thousands of people who are already building stronger, more meaningful connections with Octify.
          </p>
          <motion.button
            onClick={() => console.log('Download app clicked - would redirect to app stores')}
            className="px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-semibold text-lg flex items-center gap-2 mx-auto"
            whileHover={prefersReducedMotion ? {} : { 
              scale: 1.05, 
              boxShadow: "0 20px 40px rgba(255, 107, 53, 0.3)" 
            }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
          >
            <Heart className="w-5 h-5" />
            Download Octify
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};